// Custom Service Request Service for BookAPT
// Handles user-submitted custom service / quote requests directed at on-platform
// businesses (e.g. transportation quote requests). Persists to Firestore so the
// business owner can see them on their device, with SimpleStorage as an offline
// cache.

import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';

export interface CustomServiceRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  /** Business the request is addressed to (when known). Set when the user
   * submits the request from a specific business's detail page. */
  businessId?: string;
  businessName?: string;
  serviceName: string;
  description: string;
  preferredDate?: string;
  preferredTime?: string;
  durationEstimate?: string;
  budgetRange?: string;
  additionalDetails?: string;
  location?: string;
  zipcode?: string;
  /** Structured ride quote fields (transportation category). */
  pickupAddress?: string;
  dropoffAddress?: string;
  requestSubtype?: 'transportation_quote' | 'standard';
  status: 'pending' | 'accepted' | 'declined' | 'completed';
  createdAt: string;
  updatedAt?: string;
  notifiedBusinesses: string[]; // List of business IDs that were notified
  responses: CustomServiceResponse[];
}

export interface CustomServiceResponse {
  businessId: string;
  businessName: string;
  response: 'interested' | 'not_available' | 'need_more_info';
  message?: string;
  proposedPrice?: number;
  proposedDate?: string;
  proposedTime?: string;
  respondedAt: string;
}

const COLLECTION = 'custom_service_requests';
const FIRESTORE_TIMEOUT_MS = 4000;

class CustomServiceRequestService {
  private storageKey = COLLECTION;

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Create a new custom service request (Firestore + local cache). */
  async createRequest(
    request: Omit<CustomServiceRequest, 'id' | 'createdAt' | 'notifiedBusinesses' | 'responses'>
  ): Promise<CustomServiceRequest> {
    const newRequest: CustomServiceRequest = {
      ...request,
      id: `csr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      notifiedBusinesses: request.businessId ? [request.businessId] : [],
      responses: [],
    };

    try {
      // Persist locally first so the originator can always see their request.
      const local = await this.loadFromLocal();
      local.push(newRequest);
      await SimpleStorage.setItem(this.storageKey, JSON.stringify(local));

      // Persist to Firestore so the target business owner can see it on
      // their device. Failure here is non-fatal — the local copy is the
      // source of truth for the originating user.
      await this.saveToFirestore(newRequest);

      console.log('✅ CustomServiceRequestService: created request', newRequest.id);
      return newRequest;
    } catch (error) {
      console.error('CustomServiceRequestService: createRequest error:', error);
      throw error;
    }
  }

  /** Get all requests (Firestore-first, local fallback). */
  async getAllRequests(): Promise<CustomServiceRequest[]> {
    try {
      const remote = await this.loadFromFirestore();
      if (remote.length > 0) {
        // Merge: prefer remote, but keep any local-only records (e.g. created
        // offline before Firestore could be reached).
        const local = await this.loadFromLocal();
        const remoteIds = new Set(remote.map(r => r.id));
        const merged = [...remote, ...local.filter(r => !remoteIds.has(r.id))];
        // Refresh local cache with merged view.
        await SimpleStorage.setItem(this.storageKey, JSON.stringify(merged));
        return merged;
      }
      return await this.loadFromLocal();
    } catch (error) {
      console.error('CustomServiceRequestService: getAllRequests error:', error);
      return await this.loadFromLocal();
    }
  }

  /** Requests originated by a specific user. */
  async getRequestsByUser(userId: string): Promise<CustomServiceRequest[]> {
    const all = await this.getAllRequests();
    return all.filter(r => r.userId === userId);
  }

  /** Requests addressed to a specific business (the business owner's inbox). */
  async getRequestsForBusiness(businessId: string): Promise<CustomServiceRequest[]> {
    if (!businessId) return [];
    const all = await this.getAllRequests();
    return all
      .filter(r => r.businessId === businessId || r.notifiedBusinesses?.includes(businessId))
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }

  /** Pending requests in a specific area (legacy helper). */
  async getPendingRequestsInArea(zipcode: string): Promise<CustomServiceRequest[]> {
    const all = await this.getAllRequests();
    return all.filter(r => r.status === 'pending' && r.zipcode === zipcode);
  }

  /** Business owner replies to a request. */
  async respondToRequest(
    requestId: string,
    businessId: string,
    businessName: string,
    response: Omit<CustomServiceResponse, 'businessId' | 'businessName' | 'respondedAt'>
  ): Promise<boolean> {
    try {
      const all = await this.getAllRequests();
      const idx = all.findIndex(r => r.id === requestId);
      if (idx === -1) {
        console.warn('CustomServiceRequestService: request not found', requestId);
        return false;
      }

      const fullResponse: CustomServiceResponse = {
        ...response,
        businessId,
        businessName,
        respondedAt: new Date().toISOString(),
      };

      const updated: CustomServiceRequest = {
        ...all[idx],
        responses: [...all[idx].responses, fullResponse],
        updatedAt: new Date().toISOString(),
        status:
          response.response === 'interested'
            ? 'accepted'
            : response.response === 'not_available'
            ? 'declined'
            : all[idx].status,
      };

      all[idx] = updated;

      await SimpleStorage.setItem(this.storageKey, JSON.stringify(all));
      await this.saveToFirestore(updated);

      console.log('✅ CustomServiceRequestService: response recorded for', requestId);
      return true;
    } catch (error) {
      console.error('CustomServiceRequestService: respondToRequest error:', error);
      return false;
    }
  }

  async updateRequestStatus(
    requestId: string,
    status: CustomServiceRequest['status']
  ): Promise<boolean> {
    try {
      const all = await this.getAllRequests();
      const idx = all.findIndex(r => r.id === requestId);
      if (idx === -1) return false;

      const updated: CustomServiceRequest = {
        ...all[idx],
        status,
        updatedAt: new Date().toISOString(),
      };
      all[idx] = updated;

      await SimpleStorage.setItem(this.storageKey, JSON.stringify(all));
      await this.saveToFirestore(updated);
      return true;
    } catch (error) {
      console.error('CustomServiceRequestService: updateRequestStatus error:', error);
      return false;
    }
  }

  async getStatistics(): Promise<{
    totalRequests: number;
    pendingRequests: number;
    acceptedRequests: number;
    completedRequests: number;
    averageResponseTime: number;
  }> {
    const all = await this.getAllRequests();
    return {
      totalRequests: all.length,
      pendingRequests: all.filter(r => r.status === 'pending').length,
      acceptedRequests: all.filter(r => r.status === 'accepted').length,
      completedRequests: all.filter(r => r.status === 'completed').length,
      averageResponseTime: 0,
    };
  }

  /** Parse custom service details from natural language (legacy helper). */
  parseCustomServiceMessage(message: string): Partial<CustomServiceRequest> {
    const parsed: Partial<CustomServiceRequest> = { description: message };

    const budgetMatch = message.match(/\$(\d+)[-\s]?\$?(\d+)/);
    if (budgetMatch) {
      parsed.budgetRange = `$${budgetMatch[1]}-$${budgetMatch[2]}`;
    }

    const durationMatch = message.match(/(\d+)[-\s]?(\d+)?\s*(hour|hr|hours|hrs|min|mins|minutes)/i);
    if (durationMatch) {
      parsed.durationEstimate = durationMatch[0];
    }

    if (message.toLowerCase().includes('tomorrow')) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      parsed.preferredDate = tomorrow.toISOString().split('T')[0];
    } else if (message.toLowerCase().includes('today')) {
      parsed.preferredDate = new Date().toISOString().split('T')[0];
    }

    const timeMatch = message.match(/(\d{1,2})\s*:?\s*(\d{2})?\s*(am|pm|AM|PM)/);
    if (timeMatch) {
      parsed.preferredTime = timeMatch[0];
    }

    return parsed;
  }

  // ---------------------------------------------------------------------------
  // Internal: persistence helpers
  // ---------------------------------------------------------------------------

  private async loadFromLocal(): Promise<CustomServiceRequest[]> {
    try {
      const data = await SimpleStorage.getItem(this.storageKey);
      return data ? (JSON.parse(data) as CustomServiceRequest[]) : [];
    } catch (error) {
      console.warn('CustomServiceRequestService: loadFromLocal error:', error);
      return [];
    }
  }

  private async loadFromFirestore(): Promise<CustomServiceRequest[]> {
    try {
      if (Platform.OS === 'web') {
        const { collection, getDocs } = await import('firebase/firestore');
        const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
        if (!initializeFirebase()) return [];
        const db = getWebDb();
        if (!db) return [];

        const timeoutPromise = new Promise<CustomServiceRequest[]>(resolve =>
          setTimeout(() => resolve([]), FIRESTORE_TIMEOUT_MS)
        );
        const queryPromise = getDocs(collection(db, COLLECTION)).then(snapshot =>
          snapshot.docs.map(
            (doc: any) => ({ id: doc.id, ...doc.data() } as CustomServiceRequest)
          )
        );
        return await Promise.race([queryPromise, timeoutPromise]);
      }

      // Mobile
      const mod = require('./unifiedAuthService');
      const { getMobileFirestore, initializeFirebase } = mod;
      if (!initializeFirebase()) return [];
      const getFirestore = getMobileFirestore();
      if (!getFirestore) return [];
      const snapshot = await getFirestore().collection(COLLECTION).get();
      return snapshot.docs.map(
        (doc: any) => ({ id: doc.id, ...doc.data() } as CustomServiceRequest)
      );
    } catch (error: any) {
      console.warn(
        'CustomServiceRequestService: loadFromFirestore failed —',
        error?.message || error
      );
      return [];
    }
  }

  private async saveToFirestore(request: CustomServiceRequest): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
        if (!initializeFirebase()) return;
        const db = getWebDb();
        if (!db) return;
        await setDoc(doc(db, COLLECTION, request.id), {
          ...request,
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      const mod = require('./unifiedAuthService');
      const { getMobileFirestore, initializeFirebase } = mod;
      if (!initializeFirebase()) return;
      const getFirestore = getMobileFirestore();
      if (!getFirestore) return;
      await getFirestore().collection(COLLECTION).doc(request.id).set({
        ...request,
        updatedAt: new Date().toISOString(),
      });
    } catch (error: any) {
      console.warn(
        'CustomServiceRequestService: saveToFirestore failed —',
        error?.message || error
      );
    }
  }
}

export const customServiceRequestService = new CustomServiceRequestService();
export default customServiceRequestService;
